import { DraftPostViewMode, DraftPostStatus } from '../types/admin-types'
import { create } from 'zustand'

export const ITEMS_PER_PAGE = 20

// Define top-level admin views
export type AdminView = 'content' | 'users' | 'research'

interface AdminClientState {
    selectedUserId: string | null
    selectedDraftPostId: string | null
    selectedDraftPostIds: string[]
    viewMode: DraftPostViewMode // Refers to Draft Post view (Ideas/Posts/Feedback)
    selectedStatus: DraftPostStatus | null
    searchTerm: string
    showUserList: boolean

    // New state for user management UI
    isAddUserModalOpen: boolean
    isEditUserModalOpen: boolean
    editingUserId: string | null

    // New State for Top-Level View
    adminView: AdminView

    // Actions
    setSelectedUserId: (id: string | null) => void
    setSelectedDraftPostId: (id: string | null) => void
    toggleDraftPostSelection: (id: string) => void // For bulk selection
    clearBulkSelection: () => void
    setViewMode: (mode: DraftPostViewMode) => void
    setSelectedStatus: (status: DraftPostStatus | null) => void
    setSearchTerm: (term: string) => void
    toggleUserList: () => void

    // New actions for user management UI
    openAddUserModal: () => void
    closeAddUserModal: () => void
    openEditUserModal: (userId: string) => void
    closeEditUserModal: () => void

    // New Action for Top-Level View
    setAdminView: (view: AdminView) => void
}

export const useAdminStore = create<AdminClientState>((set, get) => ({
    // Initial state
    selectedUserId: null,
    selectedDraftPostId: null,
    selectedDraftPostIds: [],
    viewMode: DraftPostViewMode.POSTS, // Default view mode for draft posts
    selectedStatus: DraftPostStatus.POST_UNDER_REVIEW,
    searchTerm: '',
    showUserList: true,

    // Initial state for new UI elements
    isAddUserModalOpen: false,
    isEditUserModalOpen: false,
    editingUserId: null,

    // New State for Top-Level View
    adminView: 'content', // Default to content moderation view

    // Actions
    setSelectedUserId: (id) =>
        set((_state) => {
            // Logic from plan: selecting a user shows their details by default
            return {
                selectedUserId: id,
                selectedDraftPostId: null, // Clear draft post selection
                selectedDraftPostIds: [], // Clear bulk selection
                searchTerm: '', // Clear search term
                selectedStatus: null, // Clear status filter
                // Reset edit modal state if user selection changes
                isEditUserModalOpen: false,
                editingUserId: null,
            }
        }),
    setSelectedDraftPostId: (id) =>
        set({
            selectedDraftPostId: id,
        }),
    toggleDraftPostSelection: (id) => {
        const currentSelection = get().selectedDraftPostIds
        const isSelected = currentSelection.includes(id)
        set({
            selectedDraftPostIds: isSelected
                ? currentSelection.filter((postId) => postId !== id)
                : [...currentSelection, id],
        })
    },
    clearBulkSelection: () => set({ selectedDraftPostIds: [] }),
    // setViewMode remains for DraftPost view type (Ideas/Posts)
    setViewMode: (mode) =>
        set({
            viewMode: mode,
            selectedDraftPostId: null, // Clear selection when changing view mode
            selectedDraftPostIds: [], // Clear bulk selection
        }),
    setSelectedStatus: (status) =>
        set({
            selectedStatus: status,
            selectedDraftPostId: null, // Clear selection when changing filter
            selectedDraftPostIds: [], // Clear bulk selection
        }),
    setSearchTerm: (term) =>
        set({
            searchTerm: term,
            selectedDraftPostId: null, // Clear selection when searching
            selectedDraftPostIds: [], // Clear bulk selection
        }),
    toggleUserList: () => set((state) => ({ showUserList: !state.showUserList })),

    // New action implementations
    openAddUserModal: () => set({ isAddUserModalOpen: true }),
    closeAddUserModal: () => set({ isAddUserModalOpen: false }),
    openEditUserModal: (userId) =>
        set({
            isEditUserModalOpen: true,
            editingUserId: userId,
        }),
    closeEditUserModal: () => set({ isEditUserModalOpen: false, editingUserId: null }),

    // New Action Implementation
    setAdminView: (view) =>
        set({
            adminView: view,
            // Reset selections when changing main view
            selectedUserId: null,
            selectedDraftPostId: null,
            selectedDraftPostIds: [],
            // Optionally reset filters/search
            // selectedStatus: null,
            // searchTerm: '',
        }),
}))
